import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  IconButton,
  Button,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";
import { ReactComponent as CloseIcon } from "../../../../assets/images/x.svg";
import Input from "../../../common/InputField";
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import ParentCategorySelect from "../../../KnowledgeBase/Categories/ParentCategorySelect";
import { createKbCategory, resetCreateKbCategoryState } from "../../../../store/features/createCategoryToKB";
import { getAllKbCategories } from "../../../../store/features/getCategoriesToKnowledgeBase";
import { clsx } from "clsx";

const MIN_LENGTH = 3;
const MAX_LENGTH = 32;

const AddCategoryPopup = ({ open, onClose, availableCategories = [], groupId }) => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.createKbCategory);

  const [categoryName, setCategoryName] = useState("");
  const [selectedParentId, setSelectedParentId] = useState(null);
  const [visibility, setVisibility] = useState("public");

  // Only shown when user clicks Create and length is invalid (too short/long)
  const [submitError, setSubmitError] = useState("");

  const handleParentChange = (e) => {
    setSelectedParentId(e.target.value);
  };

  // Button disabled ONLY if input is empty
  const isInputEmpty = categoryName.trim().length === 0;
  const buttonDisabled = loading || isInputEmpty;

  // Validate length only on submit
  const validateLength = () => {
    const trimmed = categoryName.trim();
    const trimmedLength = trimmed.length;

    // Check for empty after trim
    if (trimmedLength === 0) {
      return "Category name is required.";
    }

    // Check minimum length
    if (trimmedLength < MIN_LENGTH) {
      return `Category name must be at least ${MIN_LENGTH} characters.`;
    }

    // Check maximum length
    if (trimmedLength > MAX_LENGTH) {
      return `Category name cannot exceed ${MAX_LENGTH} characters.`;
    }

    // Check for disallowed special characters
    // Allowed: letters, numbers, space, hyphen (-), underscore (_), apostrophe (')
    const allowedRegex = /^[a-zA-Z0-9\s\-_'\(\)]*$/;

    if (!allowedRegex.test(trimmed)) {
      return "Special characters are not allowed (only letters, numbers, spaces, -, _, and ' are permitted).";
    }

    return ""; // Valid
  };

  const handleCreate = async () => {
    const lengthError = validateLength();

    if (lengthError) {
      setSubmitError(lengthError);
      return;
    }

    // Clear any previous error
    setSubmitError("");

    try {
      await dispatch(
        createKbCategory({
          name: categoryName.trim(),
          description: "",
          parentId: selectedParentId || null,
          visibility: visibility.toLowerCase(),
          group_id: groupId,
        })
      ).unwrap();

      dispatch(getAllKbCategories({ group_id: groupId }));

      // Success: reset and close
      setCategoryName("");
      setSelectedParentId(null);
      setVisibility("public");
      setSubmitError("");
      onClose();
    } catch (err) {
      console.error("Failed to create category:", err);
    }
  };

  const handleClose = () => {
    setCategoryName("");
    setSelectedParentId(null);
    setVisibility("public");
    setSubmitError("");
    dispatch(resetCreateKbCategoryState());
    onClose();
  };

  useEffect(() => {
    if (open) {
      dispatch(resetCreateKbCategoryState());
      setSubmitError("");
      setCategoryName("");
      setSelectedParentId(null);
      setVisibility("public");
    }
  }, [open, dispatch]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      maxWidth="sm"
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "90vw", sm: "472px" },
          minWidth: { xs: "320px", sm: "472px" },
          maxWidth: { xs: "95vw", sm: "472px" },
          border: "1px solid #D5DCE1",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          maxWidth: "none",
        },
      }}
    >
      <Box sx={{ p: "32px" }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 500,
              color: "#061522",
              lineHeight: "18px",
              fontFamily: "Inter, sans-serif",
            }}
          >
            Add Category
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography
          variant="body2"
          sx={{
            mb: "27px",
            color: "#405261",
            fontFamily: "Inter",
            fontSize: 14,
            marginTop: "8px",
            fontWeight: 400,
            lineHeight: "22px",
          }}
        >
          Add a new category / subcategory to organize your articles.
        </Typography>

        {/* Server Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {typeof error === "string" ? error : "Failed to create category. Please try again."}
          </Alert>
        )}

        {/* Length Error - Only after clicking Create */}


        {/* Category Name Input */}
        <Box>
          <Input
            title="Category Name"
            placeholder="Enter category name"
            value={categoryName}
            error={submitError}
            onChange={(e) => {
              setCategoryName(e.target.value);
              // Clear error when user types
              if (submitError) setSubmitError("");
            }}
            className={clsx({
              input__error: !!submitError,
              // input__success: categoryName.trim().length > 0 && !submitError,
            })}
            autoFocus
            disabled={loading}
          />
        </Box>

        {/* Parent Category Select */}
        {/* <Box sx={{ mt: "20px" }}>
          <Typography variant="body2" sx={{ mb: "5px", color: "#405261", fontSize: 12 }}>
            Parent Category
          </Typography>
          <ParentCategorySelect
            value={selectedParentId}
            onChange={handleParentChange}
            categories={availableCategories}
            placeholder="None"
            allowNone={true}
            disabled={loading}
          />
          <Typography variant="body2" sx={{ color: "#405261", fontSize: 10, mt: "8px" }}>
            Select a parent category to create a subcategory, or choose "None" to create a top-level category.
          </Typography>
        </Box> */}

        {/* Visibility */}
        <FormControl size="small" sx={{ marginTop: "20px", width: "100%" }}>
          <Typography variant="body2" sx={{ mb: "5px", color: "#405261", fontSize: 12 }}>
            Visibility
          </Typography>
          <Select
            value={visibility}
            onChange={(e) => setVisibility(e.target.value)}
            IconComponent={Dropdown}
            displayEmpty
            disabled={loading}
            renderValue={(selected) => {
              if (!selected) {
                return <span style={{ color: "#405261", fontSize: "14px" }}>Set status</span>;
              }
              return selected.charAt(0).toUpperCase() + selected.slice(1);
            }}
            sx={{
              height: "32px",
              width: "100%",
              fontSize: "14px",
              color: "#405261",
              "& .MuiSelect-select": { padding: "0px 12px" },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  border: "1px solid #D5DCE1",
                  marginTop: "4px",
                  borderRadius: "8px",
                  "& .MuiMenuItem-root": {
                    fontSize: "14px",
                    padding: "8px 16px",
                    color: "#405261",
                    "&:hover": { color: "#061522", background: "#F9FAFA", borderRadius: "8px" },
                    "&.Mui-selected": { color: "#061522", background: "#F9FAFA", borderRadius: "8px" },
                    "&.Mui-selected:hover": { background: "#F9FAFA" },
                  },
                },
              },
            }}
          >
            {["public", "internal"].map((option) => (
              <MenuItem key={option} value={option}>
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="body2" sx={{ color: "#405261", fontSize: 10, mt: "8px" }}>
          Internal categories won't appear in the public knowledge base.
        </Typography>

        {/* Buttons */}
        <Box display="flex" gap="8px" sx={{ marginTop: "32px" }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={loading}
            sx={{
              textTransform: "none",
              borderColor: "#E5E7EB",
              color: "#405261",
              bgcolor: "#FFFFFF",
              borderRadius: "8px",
              flex: 1,
              fontSize: 14,
              height: "40px",
              fontWeight: 500,
              "&:hover": { borderColor: "#C5CED6", bgcolor: "#FFFFFF" },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={buttonDisabled}
            sx={{
              textTransform: "none",
              bgcolor: "#148dfd",
              borderRadius: "8px",
              flex: 1,
              height: "40px",
              fontSize: 14,
              fontWeight: 500,
              boxShadow: "none",
              "&:hover": { bgcolor: "#0077cc" },
              "&:disabled": { bgcolor: "#F9FAFA", color: "#93A1B0" },
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "Create Category"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default AddCategoryPopup;